
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect } from 'react';
import { Settings, Pause, Play, Zap } from 'lucide-react';
import { formatCompactCurrency } from '../../../utils/currencyFormatter';
import { useTutorialContext } from '../../../tutorial/TutorialProvider';
import { useDeviceType } from '../../../hooks/useDeviceType';
import type { CompanyState } from '../../../types/company';
import { DASHBOARD_CONSTANTS } from '../../../config/gameConstants';
import type { DashboardTab } from '../../../types/dashboard';

interface GameControls {
  isPaused: boolean;
  gameSpeed: number;
}

interface GameSpeed {
  label: string;
  multiplier: number;
}

interface DashboardNavbarProps {
  companyState: CompanyState;
  gameControls: GameControls;
  onBack: () => void;
  companyName?: string;
  onTogglePause?: () => void;
  onChangeSpeed?: (speed: GameSpeed) => void;
  availableSpeeds?: GameSpeed[];
  activeTab?: DashboardTab;
  onTabChange?: (tab: DashboardTab) => void;
}

function DashboardNavbarComponent({ companyState, gameControls, onBack, companyName, onTogglePause, onChangeSpeed, availableSpeeds, activeTab = DASHBOARD_CONSTANTS.defaultTab, onTabChange }: DashboardNavbarProps) {
  const { isRunning: isTutorialRunning } = useTutorialContext();
  const { isTablet, hasTouch } = useDeviceType();

  const handleTabClick = (tab: DashboardTab) => {
    if (onTabChange && !isTutorialRunning) {
      onTabChange(tab);
    }
  };

  // Add defensive checks
  if (!companyState || !gameControls) {
    return (
      <div className="grid grid-cols-[300px_1fr_300px] gap-4 items-center">
        <div className="bg-theme-bg-tertiary border border-theme-border rounded-lg p-3">
          <div className="font-bold text-sm">Loading...</div>
        </div>
        <div className="bg-theme-bg-tertiary border border-theme-border rounded-lg p-3">
          <div className="text-center text-theme-text-muted">Loading navigation...</div>
        </div>
        <div className="bg-theme-bg-tertiary border border-theme-border rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="text-right flex-1">
              <div className="font-bold text-sm text-theme-success">Loading...</div>
            </div>
            <button
              onClick={!isTutorialRunning ? onBack : undefined}
              className={`ml-3 p-2 text-theme-text-secondary rounded transition-colors ${
                isTutorialRunning
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:text-theme-text-primary hover:bg-theme-bg-tertiary'
              }`}
              title="Back to Menu"
              disabled={isTutorialRunning}
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`grid gap-4 items-center ${
      isTablet
        ? 'grid-cols-[1fr_2fr_1fr]'
        : 'grid-cols-[300px_1fr_300px]'
    }`}>
      {/* Company Info */}
      <div className="bg-theme-bg-tertiary border border-theme-border rounded-lg p-3" data-tutorial="company-info">
        <div className="font-bold text-sm mb-2 text-theme-text-primary">{companyName || 'Your Company'}</div>
        {/* Game Controls */}
        <div className="flex items-center gap-1">
          {/* Pause/Play button */}
          {onTogglePause && (
            <button
              onClick={!isTutorialRunning ? onTogglePause : undefined}
              data-testid="pause-button"
              disabled={isTutorialRunning}
              className={`w-7 h-7 rounded transition-colors flex items-center justify-center ${
                gameControls.isPaused
                  ? 'bg-theme-accent text-white'
                  : 'text-theme-text-secondary hover:bg-theme-bg-secondary'
              }`}
              title={gameControls.isPaused ? 'Resume' : 'Pause'}
            >
              {gameControls.isPaused ? (
                <Play className="w-3.5 h-3.5" />
              ) : (
                <Pause className="w-3.5 h-3.5" />
              )}
            </button>
          )}
          {/* Speed buttons */}
          {onChangeSpeed && availableSpeeds && availableSpeeds.filter(speed => speed.multiplier > 0).map((speed) => {
            const isActive = !gameControls.isPaused && gameControls.gameSpeed === speed.multiplier;
            const isMax = speed.multiplier === 8;
            return (
              <button
                key={speed.multiplier}
                data-testid={`speed-${speed.multiplier}x`}
                onClick={() => {
                  if (!isTutorialRunning) {
                    if (gameControls.isPaused && onTogglePause) {
                      onTogglePause();
                    }
                    onChangeSpeed(speed);
                  }
                }}
                className={`w-7 h-7 text-xs font-medium rounded transition-colors flex items-center justify-center ${
                  isTutorialRunning
                    ? 'text-theme-text-muted cursor-not-allowed'
                    : isActive
                    ? 'bg-theme-accent text-white'
                    : 'text-theme-text-secondary hover:bg-theme-bg-secondary'
                }`}
                disabled={isTutorialRunning}
                title={`${speed.label || speed.name || ''} Speed (${speed.multiplier}x)`}
              >
                {isMax ? (
                  <Zap className="w-3.5 h-3.5" />
                ) : (
                  `${speed.multiplier}x`
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Navigation */}
      <div className="bg-theme-bg-tertiary border border-theme-border rounded-lg p-3">
        <div className={`flex justify-center ${isTablet ? 'gap-1 flex-wrap' : 'gap-1'}`}>
          {DASHBOARD_CONSTANTS.tabs.map((tab) => (
            <button
              key={tab}
              data-testid={`nav-${tab.toLowerCase()}`}
              data-tutorial={`${tab.toLowerCase()}-tab`}
              onClick={() => handleTabClick(tab)}
              className={`rounded font-medium transition-colors ${
                isTablet || hasTouch
                  ? 'px-3 py-2 min-h-[44px] text-sm'
                  : 'px-2 py-1.5 text-xs'
              } ${
                isTutorialRunning
                  ? activeTab === tab
                    ? 'bg-theme-accent text-theme-bg-primary cursor-not-allowed'
                    : 'bg-transparent text-theme-text-muted cursor-not-allowed'
                  : activeTab === tab
                    ? 'bg-theme-accent text-theme-bg-primary'
                    : 'bg-transparent text-theme-text-secondary hover:bg-theme-bg-tertiary'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Cash Info with Settings */}
      <div className="bg-theme-bg-tertiary border border-theme-border rounded-lg p-3" data-tutorial="cash-display">
        <div className="flex items-center justify-between">
          <div className="text-left">
            <div className="text-xs text-theme-text-secondary" data-testid="game-date">
              {new Date(companyState.year, companyState.month - 1, companyState.day).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })}
            </div>
          </div>
          <div className="text-right flex-1">
            <div className="font-bold text-sm text-theme-success" data-testid="cash-balance">
              {companyState.cash > 100000000 
                ? formatCompactCurrency(companyState.cash)
                : `$${companyState.cash.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
              }
            </div>
            <div className="text-xs text-theme-text-secondary">
              {formatCompactCurrency(companyState.dailyStats.profit * 30)} monthly
            </div>
          </div>
          <button
            onClick={!isTutorialRunning ? onBack : undefined}
            data-testid="settings-button"
            className={`ml-3 p-2 text-theme-text-secondary rounded transition-colors border border-theme-border ${
              isTutorialRunning
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:text-theme-text-primary hover:bg-theme-bg-tertiary'
            }`}
            title="Settings & Menu"
            disabled={isTutorialRunning}
          >
            <Settings className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export const DashboardNavbar = DashboardNavbarComponent;

